import {
  createColumnHelper,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  type OnboardingParent,
  useGetParentsOnboardingBatchesById,
} from '~entities/parent/api/get-parents-onboarding-batches-by-id';

import { Card, CardContent } from '~/shared/shadcn/ui/card';
import {
  EnrollioTable,
  EnrollioTableSelectFilter,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Badge } from '~shared/shadcn/ui/badge';

// Create a column helper
const columnHelper = createColumnHelper<OnboardingParent>();

interface ParentsOnboardingBatchesByIdTableProps {
  batchId: string;
}

export const ParentsOnboardingBatchesByIdTable = ({
  batchId,
}: ParentsOnboardingBatchesByIdTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  // Create query params for API
  const queryParams = useMemo(() => {
    return {
      batchId,
      page: pagination.pageIndex + 1, // API usually uses 1-indexed pages
      limit: pagination.pageSize,
    };
  }, [batchId, pagination.pageIndex, pagination.pageSize]);

  const { data, isFetching } = useGetParentsOnboardingBatchesById(queryParams);

  const columns = useMemo(
    () => [
      columnHelper.accessor('firstName', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='First Name'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('lastName', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Last Name'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('email', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Email'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Status'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => {
          const status = info.getValue();
          let badgeClass = '';

          switch (status.toLowerCase()) {
            case 'success':
              badgeClass = 'bg-green-100 text-green-800';
              break;
            case 'failed':
              badgeClass = 'bg-red-100 text-red-800';
              break;
            case 'pending':
              badgeClass = 'bg-amber-100 text-amber-800';
              break;
            default:
              badgeClass = 'bg-gray-100 text-gray-800';
          }

          return (
            <div className='px-4'>
              <Badge variant='outline' className={badgeClass}>
                {status}
              </Badge>
            </div>
          );
        },
      }),
    ],
    [],
  );

  return (
    <Card>
      <CardContent className='pt-6'>
        {isFetching ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='size-8 animate-spin text-primary' />
          </div>
        ) : (
          <EnrollioTable
            ariaLabel='Parents in onboarding batch'
            totalLabel='Parents'
            filterHeader={(table) => {
              const statusColumn = table.getColumn('status');
              const status = statusColumn?.getFilterValue();
              return (
                <EnrollioTableSelectFilter
                  value={typeof status === 'string' ? status : 'all'}
                  onChange={(value) => {
                    statusColumn?.setFilterValue(
                      value === 'all' ? undefined : value,
                    );
                  }}
                  placeholder='Filter by status'
                  options={[
                    { label: 'All statuses', value: 'all' },
                    { label: 'Success', value: 'success' },
                    { label: 'Failed', value: 'failed' },
                    { label: 'Pending', value: 'pending' },
                  ]}
                />
              );
            }}
            columns={columns}
            data={data?.parents ?? []}
            sorting={sorting}
            onSortingChange={setSorting}
            pagination={pagination}
            onPaginationChange={setPagination}
            manualPagination
            rowCount={data?.pagination.total ?? 0}
          />
        )}
      </CardContent>
    </Card>
  );
};
